import type { SlugCheckResponse } from '@dnx/contracts';
import { formatCnpj, isValidCnpj, slugify } from '@dnx/domain';
import { Alert, Button, Card, Field, Input, Select } from '@dnx/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, CircleCheck, CircleX } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api } from '../lib/api.js';
import { baseHost } from '../lib/host.js';

function useDebounced<T>(value: T, ms = 300): T {
  const [v, setV] = useState(value);
  useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t); }, [value, ms]);
  return v;
}

export function OrganizationNewPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [tradeName, setTradeName] = useState('');
  const [legalName, setLegalName] = useState('');
  const [cnpj, setCnpj] = useState('');
  const [slug, setSlug] = useState('');
  const [slugEdited, setSlugEdited] = useState(false);
  const [plan, setPlan] = useState('pilot');
  const [submitted, setSubmitted] = useState(false);

  const effectiveSlug = slugEdited ? slug : slugify(tradeName);
  const debouncedSlug = useDebounced(effectiveSlug);
  const slugCheck = useQuery({
    queryKey: ['platform', 'slug', debouncedSlug],
    queryFn: () => api<SlugCheckResponse>(`/platform/slug-check?slug=${encodeURIComponent(debouncedSlug)}`),
    enabled: debouncedSlug.length > 0,
  });

  const errors = {
    tradeName: tradeName.trim().length < 2 ? 'Informe o nome fantasia' : undefined,
    legalName: legalName.trim().length < 2 ? 'Informe a razão social' : undefined,
    cnpj: !isValidCnpj(cnpj) ? 'CNPJ inválido: confira os dígitos' : undefined,
    slug: slugCheck.data && !slugCheck.data.available ? slugCheck.data.message : undefined,
  };
  const valid = !errors.tradeName && !errors.legalName && !errors.cnpj && slugCheck.data?.available === true && debouncedSlug === effectiveSlug;

  const create = useMutation({
    mutationFn: () => api<{ organizationId: string }>('/platform/organizations', {
      method: 'POST',
      body: { tradeName: tradeName.trim(), legalName: legalName.trim(), cnpj: cnpj.replace(/\D/g, ''), slug: effectiveSlug, plan },
    }),
    onSuccess: async (res) => {
      await qc.invalidateQueries({ queryKey: ['platform', 'orgs'] });
      void navigate({ to: '/clinicas/$id', params: { id: res.organizationId }, search: { aba: 'equipe' } });
    },
  });

  return (
    <div className="mx-auto max-w-2xl">
      <Link to="/clinicas" className="-ml-2 inline-flex h-9 items-center gap-1 rounded-md px-2 text-sm text-fg-muted hover:text-fg"><ArrowLeft className="size-4" />Clínicas</Link>
      <h1 className="mt-2 text-2xl font-semibold tracking-tight text-fg">Nova clínica</h1>
      <p className="mt-1 text-sm text-fg-muted">A clínica recebe na hora o endereço do portal e a marca padrão, que pode ser ajustada depois.</p>

      <Card className="mt-6 p-5">
        <form className="grid gap-5" noValidate onSubmit={(e) => { e.preventDefault(); setSubmitted(true); if (valid) create.mutate(); }}>
          {create.error && <Alert tone="danger" title="Não foi possível criar">{create.error.message}</Alert>}
          <Field label="Nome fantasia" hint="Como pacientes conhecem a clínica." error={submitted ? errors.tradeName : undefined}>
            <Input autoFocus value={tradeName} onChange={(e) => setTradeName(e.target.value)} placeholder="Clínica Exemplo" />
          </Field>
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Razão social" error={submitted ? errors.legalName : undefined}>
              <Input value={legalName} onChange={(e) => setLegalName(e.target.value)} placeholder="Clínica Exemplo LTDA" />
            </Field>
            <Field label="CNPJ" error={(submitted || cnpj.replace(/\D/g, '').length === 14) ? errors.cnpj : undefined}>
              <Input inputMode="numeric" value={cnpj} onChange={(e) => setCnpj(formatCnpj(e.target.value))} placeholder="00.000.000/0000-00" />
            </Field>
          </div>
          <Field label="Endereço do portal" error={errors.slug}
            hint={<span className="flex items-center gap-1.5">
              {slugCheck.data?.available && debouncedSlug === effectiveSlug
                ? <><CircleCheck className="size-3.5 text-success" aria-hidden />Disponível: <code className="font-mono text-fg">{effectiveSlug}.{baseHost()}</code></>
                : effectiveSlug ? <><code className="font-mono">{effectiveSlug}.{baseHost()}</code></> : 'Gerado a partir do nome fantasia.'}
              {slugCheck.data && !slugCheck.data.available && <CircleX className="size-3.5 text-danger" aria-hidden />}
            </span>}>
            <Input className="font-mono" value={effectiveSlug}
              onChange={(e) => { setSlugEdited(true); setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '')); }} />
          </Field>
          <Field label="Plano">
            <Select value={plan} onChange={(e) => setPlan(e.target.value)}>
              <option value="pilot">Piloto</option>
              <option value="standard">Padrão</option>
              <option value="network">Rede (várias unidades)</option>
            </Select>
          </Field>
          <div className="flex justify-end gap-2 border-t border-border pt-5">
            <Link to="/clinicas"><Button variant="ghost">Cancelar</Button></Link>
            <Button type="submit" loading={create.isPending} disabled={submitted && !valid}>Criar clínica</Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
